import { generateApiRoute } from '@klp/core/api/helpers';
import { baseApi } from '@klp/core/api';
import {
    reportMappingApiName,
    reportMappingReport,
    reportMappingKunde,
    reportMappingBygg,
    reportMappingReportType,
} from '../config';
import type { Bygg, Kunde } from '@forvalter/types/ForvalterCommonDAL';
import type {
    ReportDto,
    ReportMapping,
    ReportMappingSearchParams,
    ReportWithMappingDto,
} from '../types';

export const reportMappingApi = baseApi.injectEndpoints({
    endpoints: (build) => ({
        getEnerginetReportmappings: build.query<
            Array<ReportWithMappingDto>,
            void
        >({
            query: () => ({
                url: generateApiRoute([reportMappingApiName]),
                method: 'GET',
            }),
            providesTags: ['ReportMappings'],
        }),
        getEnerginetReports: build.query<Array<ReportDto>, void>({
            query: () => ({
                url: generateApiRoute([
                    reportMappingApiName,
                    reportMappingReport,
                ]),
                method: 'GET',
            }),
            providesTags: ['ReportMappingReports'],
        }),
        getKunder: build.query<Array<Kunde>, ReportMappingSearchParams>({
            query: ({ navnOrNummer }) => ({
                url: generateApiRoute([
                    reportMappingApiName,
                    reportMappingKunde,
                ]),
                params: { navnOrNummer },
                method: 'GET',
            }),
            providesTags: ['ReportMappingKunder'],
        }),
        getRapportBygg: build.query<Array<Bygg>, ReportMappingSearchParams>({
            query: ({ navnOrNummer }) => ({
                url: generateApiRoute([
                    reportMappingApiName,
                    reportMappingBygg,
                ]),
                params: { navnOrNummer },
                method: 'GET',
            }),
            providesTags: ['ReportMappingBygg'],
        }),
        getReportTypes: build.query<Array<string>, void>({
            query: () => ({
                url: generateApiRoute([
                    reportMappingApiName,
                    reportMappingReportType,
                ]),
                method: 'GET',
            }),
            providesTags: ['ReportMappingTypes'],
        }),
        upsertReportMapping: build.mutation<void, ReportMapping>({
            query: (reportMapping) => ({
                url: generateApiRoute([reportMappingApiName]),
                method: 'POST',
                body: reportMapping,
            }),
            invalidatesTags: ['ReportMappings', 'ReportMappingReports'],
        }),
        deleteReportMapping: build.mutation<void, { reportId: string }>({
            query: ({ reportId }) => ({
                url: generateApiRoute([reportMappingApiName, reportId]),
                method: 'DELETE',
            }),
            invalidatesTags: ['ReportMappings', 'ReportMappingReports'],
        }),
        getReportMapping: build.query<
            ReportWithMappingDto,
            { reportId: string }
        >({
            query: ({ reportId }) => ({
                url: generateApiRoute([reportMappingApiName, reportId]),
                method: 'GET',
            }),
            providesTags: (result, error, { reportId }) => [
                { type: 'ReportMapping', id: reportId },
            ],
        }),
        transferReport: build.mutation<void, { reportId: string }>({
            query: ({ reportId }) => ({
                url: generateApiRoute([
                    reportMappingApiName,
                    reportMappingReport,
                    'transfer',
                    reportId,
                ]),
                method: 'GET',
            }),
        }),
    }),
});

export const {
    useGetEnerginetReportmappingsQuery,
    useLazyGetEnerginetReportmappingsQuery,
    useGetEnerginetReportsQuery,
    useLazyGetEnerginetReportsQuery,
    useGetKunderQuery,
    useLazyGetKunderQuery,
    useGetRapportByggQuery,
    useLazyGetRapportByggQuery,
    useGetReportTypesQuery,
    useLazyGetReportTypesQuery,
    useUpsertReportMappingMutation,
    useDeleteReportMappingMutation,
    useGetReportMappingQuery,
    useLazyGetReportMappingQuery,
    useTransferReportMutation,
} = reportMappingApi;
